// llplExport.js — builds the Atterberg-limits Excel report (same layout as the lab's template)
// and the flow-curve graph. Runs in the browser; `exceljs` is loaded only when Save is clicked.
//   npm install exceljs

const THIN = { style: 'thin', color: { argb: 'FF000000' } };
const BOX = { top: THIN, left: THIN, bottom: THIN, right: THIN };
const font = (size = 10, bold = false) => ({ name: 'Arial', size, bold });
const num = (v) => (typeof v === 'number' && Number.isFinite(v));
const moist = (w, d, t) => ((w - d) / (d - t)) * 100;
const mean = (xs) => xs.reduce((a, b) => a + b, 0) / xs.length;
const asCell = (s) => (/^\d+$/.test(String(s).trim()) ? Number(s) : String(s ?? '').trim());
const utcDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)); // UTC so Excel shows the same calendar day in any time zone
};

// ---------------------------------------------------------------- graph
// Draws the flow curve (water content vs log blows) on any canvas: browser <canvas> or a node canvas.
export function drawFlowCurve(canvas, { points, llRaw, flowIndex, LL, r2, title }) {
  const W = 1240, H = 800;
  canvas.width = W; canvas.height = H;
  const g = canvas.getContext('2d');
  g.fillStyle = '#ffffff'; g.fillRect(0, 0, W, H);

  const m = { l: 130, r: 60, t: 100, b: 130 };
  const px = (N) => m.l + (Math.log10(N) - 1) * (W - m.l - m.r); // log scale 10 → 100 blows
  const ws = points.map((p) => p.w).concat([llRaw]);
  let lo = Math.min(...ws) - 2, hi = Math.max(...ws) + 2;
  const step = hi - lo <= 10 ? 1 : hi - lo <= 20 ? 2 : hi - lo <= 50 ? 5 : 10;
  lo = Math.floor(lo / step) * step; hi = Math.ceil(hi / step) * step;
  const py = (w) => H - m.b - ((w - lo) / (hi - lo)) * (H - m.t - m.b);
  const fit = (N) => llRaw - flowIndex * (Math.log10(N) - Math.log10(25));

  g.font = 'bold 34px Arial'; g.fillStyle = '#111'; g.textAlign = 'center';
  g.fillText(title, W / 2, 55);

  // grid + tick labels
  g.lineWidth = 1; g.strokeStyle = '#d9d9d9'; g.fillStyle = '#333'; g.font = '24px Arial';
  [10, 15, 20, 25, 30, 40, 50, 60, 80, 100].forEach((N) => {
    g.beginPath(); g.moveTo(px(N), m.t); g.lineTo(px(N), H - m.b); g.stroke();
    g.textAlign = 'center'; g.fillText(String(N), px(N), H - m.b + 34);
  });
  for (let w = lo; w <= hi + 1e-9; w += step) {
    g.beginPath(); g.moveTo(m.l, py(w)); g.lineTo(W - m.r, py(w)); g.stroke();
    g.textAlign = 'right'; g.fillText(String(w), m.l - 14, py(w) + 8);
  }
  g.strokeStyle = '#111'; g.lineWidth = 2.5; g.strokeRect(m.l, m.t, W - m.l - m.r, H - m.t - m.b);

  g.fillStyle = '#111'; g.font = '28px Arial'; g.textAlign = 'center';
  g.fillText('No. of blows (log scale)', (m.l + W - m.r) / 2, H - 30);
  g.save(); g.translate(40, (m.t + H - m.b) / 2); g.rotate(-Math.PI / 2);
  g.fillText('Water content (%)', 0, 0); g.restore();

  g.save();
  g.beginPath(); g.rect(m.l, m.t, W - m.l - m.r, H - m.t - m.b); g.clip();
  // best-fit flow line
  g.strokeStyle = '#1d4ed8'; g.lineWidth = 4;
  g.beginPath(); g.moveTo(px(10), py(fit(10))); g.lineTo(px(100), py(fit(100))); g.stroke();
  // LL construction at 25 blows
  g.setLineDash([14, 10]); g.strokeStyle = '#b91c1c'; g.lineWidth = 3;
  g.beginPath(); g.moveTo(px(25), H - m.b); g.lineTo(px(25), py(llRaw)); g.lineTo(m.l, py(llRaw)); g.stroke();
  g.setLineDash([]);
  // test points
  g.fillStyle = '#111';
  points.forEach((p) => { g.beginPath(); g.arc(px(p.N), py(p.w), 10, 0, Math.PI * 2); g.fill(); });
  g.restore();

  g.fillStyle = '#b91c1c'; g.font = 'bold 28px Arial'; g.textAlign = 'left';
  g.fillText(`LL = ${LL} %  (at 25 blows)`, px(25) + 16, py(llRaw) - 16);
  g.fillStyle = '#333'; g.font = '24px Arial'; g.textAlign = 'right';
  g.fillText(`Flow index = ${flowIndex.toFixed(1)}    R² = ${r2.toFixed(3)}`, W - m.r - 16, m.t + 36);
  return canvas;
}

// ---------------------------------------------------------------- workbook
export function addLLPLSheet(wb, { inputs, header, result, imageBase64 }) {
  const ll = inputs.ll, pl = inputs.pl, np = inputs.plNotObtainable;
  const nLL = Math.max(ll.length, 1);
  const nPLcols = np ? 2 : Math.max(pl.length, 2);
  const LLc = 5;                       // first LL column (E)
  const PLc = LLc + nLL;               // first PL column
  const last = PLc + nPLcols - 1;      // last column used

  const ws = wb.addWorksheet('LL & PL', {
    pageSetup: { paperSize: 9, orientation: 'portrait', fitToPage: true, fitToWidth: 1, fitToHeight: 1 },
  });
  [10.85, 10.85, 4.57, 9.14].forEach((w, i) => { ws.getColumn(i + 1).width = w; });
  for (let c = 5; c <= last; c++) ws.getColumn(c).width = 12.71;

  // writes style to every cell of the range first, then merges (so merged cells keep their borders)
  const area = (r1, c1, r2, c2, value, o = {}) => {
    for (let r = r1; r <= r2; r++) for (let c = c1; c <= c2; c++) {
      const cell = ws.getCell(r, c);
      cell.border = o.border ?? BOX;
      cell.font = o.font ?? font();
      cell.alignment = o.align ?? { horizontal: 'center', vertical: 'middle', wrapText: true };
    }
    const top = ws.getCell(r1, c1);
    if (value !== undefined && value !== null && value !== '') top.value = value;
    if (o.numFmt) top.numFmt = o.numFmt;
    if (r2 > r1 || c2 > c1) ws.mergeCells(r1, c1, r2, c2);
  };
  const left = { horizontal: 'left', vertical: 'middle', wrapText: true };
  const right = { horizontal: 'right', vertical: 'middle', wrapText: true };

  [33.75, 21, 16.5, 12, 20.25, 20.25, 20.25, 18].forEach((h, i) => { ws.getRow(i + 1).height = h; });
  for (let r = 9; r <= 17; r++) ws.getRow(r).height = 24.75;
  for (let r = 18; r <= 43; r++) ws.getRow(r).height = 12;

  // title block
  area(1, 1, 1, 2, 'Name of  Project :- ', { font: font(11, true), align: left });
  area(1, 3, 1, last, header.project, { font: font(11), align: left });
  area(2, 1, 2, 2, 'Issue to ', { font: font(11, true), align: left });
  area(2, 3, 2, last, header.issueTo, { font: font(11), align: left });
  area(3, 1, 3, last, "ATTERBERG'S  LIMITS", { font: font(12, true) });
  area(4, 1, 4, last, '(As per IS :2720: Part 5: 1985)', { font: font(10, true) });

  const info = (r, l1, v1, l2, v2, dateFmt) => {
    area(r, 1, r, 2, l1, { align: left });
    area(r, 3, r, last - 4, v1, { align: left });
    area(r, last - 3, r, last - 2, l2, { align: left });
    area(r, last - 1, r, last, v2, dateFmt ? { numFmt: 'dd/mm/yyyy' } : {});
  };
  info(5, 'Location/Offset', header.location, 'Date of Receiving', utcDate(header.receivedOn), true);
  info(6, 'Type of material', header.material, 'Date  of Testing', utcDate(header.testedOn), true);
  info(7, 'Sample No.', header.sampleNo, 'Tested by', header.testedBy);

  area(8, 1, 8, 4, 'DESCRIPTION', { font: font(12, true) });
  area(8, LLc, 8, PLc - 1, 'LIQUID LIMIT', { font: font(12, true) });
  area(8, PLc, 8, last, 'PLASTIC LIMIT', { font: font(12, true) });

  const labels = ['Determination no', 'Container no.(small)', 'wt. of container+wet soil (gm)',
    'wt. of container+dry soil (gm)', 'wt. of water (gm)', 'Wt. of  container  (gm)',
    'Wt. of dry soil  (gm)', 'Moisture content (%)', 'No of blows/Penetration'];
  labels.forEach((t, i) => area(9 + i, 1, 9 + i, 4, t, { align: left }));
  // empty bordered cells first, so every grid cell exists
  for (let r = 9; r <= 17; r++) for (let c = LLc; c <= last; c++) area(r, c, r, c, undefined);

  const column = (c, k, row) => {
    const L = ws.getColumn(c).letter;
    const w = row.wetG, d = row.dryG, t = row.tareG;
    ws.getCell(9, c).value = k + 1;
    ws.getCell(10, c).value = asCell(row.cont);
    ws.getCell(11, c).value = w; ws.getCell(11, c).numFmt = '0.00';
    ws.getCell(12, c).value = d; ws.getCell(12, c).numFmt = '0.00';
    ws.getCell(13, c).value = { formula: `${L}11-${L}12`, result: w - d }; ws.getCell(13, c).numFmt = '0.00';
    ws.getCell(14, c).value = t; ws.getCell(14, c).numFmt = '0.00';
    ws.getCell(15, c).value = { formula: `${L}12-${L}14`, result: d - t }; ws.getCell(15, c).numFmt = '0.00';
    ws.getCell(16, c).value = { formula: `${L}13/${L}15*100`, result: moist(w, d, t) }; ws.getCell(16, c).numFmt = '0.00';
  };
  ll.forEach((row, j) => { column(LLc + j, j, row); ws.getCell(17, LLc + j).value = row.blows; });
  if (!np) pl.forEach((row, k) => column(PLc + k, k, row));

  const plAvg = !np && pl.length ? mean(pl.map((r) => moist(r.wetG, r.dryG, r.tareG))) : null;
  area(17, PLc, 17, last, np ? 'NP' : plAvg === null ? '' : Number(plAvg.toFixed(2)),
    { font: font(10, true), numFmt: '0.00' });

  // remarks line (small print)
  const hist = { natural: 'Natural', 'air-dried': 'Air-dried', 'oven-dried': 'Oven-dried', unknown: 'Unknown' }[inputs.soilHistory] || '';
  area(18, 1, 18, last,
    `Soil history: ${hist}   |   Soaking after mixing: ${inputs.soakHours} h   |   Checked by: ${header.checkedBy}`,
    { font: font(8), align: left, border: {} });

  // results block (right-hand side, like the template)
  const rl = last - 1, rv = last;
  const box = (r, label, value, fmt) => {
    area(r, rl, r + 1, rl, label, { font: font(10), align: right, border: {} });
    area(r, rv, r + 1, rv, value, { font: font(10, true), numFmt: fmt, border: {} });
  };
  box(22, 'Liquid Limit\nLL  = ', result.LL, '0');
  box(25, 'Plastic Limit\nPL = ', np ? 'NP' : result.PL, '0');
  box(28, 'Plasticity Index,P.I = ', np ? 'NP' : result.PI, '0');
  area(31, rl, 31, rl, 'Flow index', { font: font(9), align: right, border: {} });
  area(31, rv, 31, rv, Number(result.flowIndex.toFixed(1)), { font: font(9, true), numFmt: '0.0', border: {} });
  area(32, rl, 32, rl, 'R²', { font: font(9), align: right, border: {} });
  area(32, rv, 32, rv, Number(result.r2.toFixed(3)), { font: font(9, true), numFmt: '0.000', border: {} });

  // graph, left of the results block
  if (imageBase64) {
    const id = wb.addImage({ base64: imageBase64, extension: 'png' });
    ws.addImage(id, { tl: { col: 0, row: 18 }, ext: { width: 620, height: 400 } });
  }

  area(44, 1, 47, last, 'Authorised Signatory',
    { font: font(14, true), align: { horizontal: 'right', vertical: 'bottom' }, border: {} });
  return ws;
}

// ---------------------------------------------------------------- portal hook
// Called by the portal for each selected test: adds the sheet, returns extra files (graph PNG) to download.
export const llplExporter = {
  async addSheet(wb, { header, data }) {
    const { inputs, result } = data;
    const canvas = drawFlowCurve(document.createElement('canvas'), {
      points: result.llPoints, llRaw: result.llRaw, flowIndex: result.flowIndex, LL: result.LL, r2: result.r2,
      title: `Flow curve — ${header.sampleNo || ''} ${header.location || ''}`.trim(),
    });
    addLLPLSheet(wb, { inputs, header, result, imageBase64: canvas.toDataURL('image/png') });
    const png = await new Promise((res) => canvas.toBlob(res, 'image/png'));
    return [{ suffix: 'LL_flow_curve.png', blob: png }];
  },
};
